<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, PropType, ref, watch } from "vue";
import {
  ITaskParameters,
  TaskDifficultiesService,
  TaskParametersTemplateService,
  TaskPrioritiesService,
  TaskStatusesService,
  TaskStatusesServiceAdapter,
  TaskTagsService,
  TaskTypesService,
} from "./adapter-service";
import { TaskParameterModel, TaskParameterTypeEnum } from "@/components/task-parameter";
import { ParametersTemplateModel } from "./models";
import {
  TirPmButtonVariantEnum,
  TirPmButton,
  TirPmButtonSizeEnum,
  Tabs,
  Tab,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
} from "tir-components";
import {
  ParametersEditForm,
  TemplatesListForm,
  TemplateEditForm,
  TemplateImportForm,
} from "./forms";
import { TirPmTabsVariantEnum } from "tir-pm-tabs/src/Enums";
import { TaskParametersLeave } from "./components";
import ApiService from "@/api";

/**Сервис для использования api шаблонов */
const templateService = new TaskParametersTemplateService();
/**Сервис для использования API */
const apiService = ref<ITaskParameters>();

/**Параметры */
const props = defineProps({
  /**Id очереди */
  entityId: { type: String || Number },
  /**Тип настройки */
  type: { type: Number as PropType<TaskParameterTypeEnum> },
  /**Имя области */
  namespaceName: { type: String },
});
/**События */
const emit = defineEmits<{
  /**Активен режим импорта */
  import: [isImport: boolean];
}>();

/**Список шаблонов */
const templates = ref<ParametersTemplateModel[]>([]);
/**Список параметров */
const parameters = ref<TaskParameterModel[]>([]);
/**Редактируемый список параметров */
const editParameters = ref<TaskParameterModel[]>([]);
/**Заголовок формы */
const title = ref("");
/**Текущий открытый таб */
const currentTab = ref(0);
/**Редактирующийся шаблон */
const editingTemplate = ref<ParametersTemplateModel>();
/**Активна форма импорта шаблонов из других очередей */
const isImport = ref(false);
/**Окно для подтверждения закрытия настроек параметров */
const $taskParametersLeave = ref<InstanceType<typeof TaskParametersLeave>>();

/**Параметр рабочей области */
const isNamespaceParameter = computed(
  () => props.type == TaskParameterTypeEnum.Type || props.type == TaskParameterTypeEnum.Difficulty,
);
/**Есть изменения в параметрах */
const haveChanges = computed(() => {
  const jsonOriginal = JSON.stringify(parameters.value);
  const jsonEdited = JSON.stringify(editParameters.value);
  return jsonOriginal != jsonEdited;
});
/**Табы */
const tabs = computed(() => {
  return [
    { Id: 0, Name: title.value },
    { Id: 1, Name: "Мои шаблоны" },
  ];
});

/**Отслеживание изменения типа параметра */
watch(() => props.type, updateByType);

/**Перед рендером компонента */
onBeforeMount(() => {
  Promise.allSettled([updateByType(), getTemplates()]);
  window.addEventListener("beforeunload", windowOnBeforeUnload);
});
/**Перед удалением */
onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", windowOnBeforeUnload);
});

/**Если не сохранены изменения, открывать окно для подтверждения закрытия страницы */
function windowOnBeforeUnload(event: BeforeUnloadEvent) {
  if (haveChanges.value) {
    event.preventDefault();
    event.returnValue = true;
  }
}
/**Обновление компонента по типу */
async function updateByType() {
  switch (props.type) {
    case TaskParameterTypeEnum.Status:
      title.value = "Статусы";
      apiService.value = new TaskStatusesServiceAdapter();
      break;
    case TaskParameterTypeEnum.Priority:
      title.value = "Приоритеты";
      apiService.value = new TaskPrioritiesService();
      break;
    case TaskParameterTypeEnum.Tag:
      title.value = "Теги";
      apiService.value = new TaskTagsService();
      break;
    case TaskParameterTypeEnum.Type:
      title.value = "Типы";
      apiService.value = new TaskTypesService();
      break;
    case TaskParameterTypeEnum.Difficulty:
      title.value = "Сложность";
      apiService.value = new TaskDifficultiesService();
      break;
  }

  getList();
}
/**Получение списка задач */
const getList = async () => {
  const response = await apiService.value.GetList(props.entityId);
  if (response.IsSuccess) {
    setParametersArrays(response.Value);
  }
};
/**Редактирование элемента */
const onUpsertParameter = (_parameter: TaskParameterModel) => {
  if (editParameters.value.length == 0) {
    _parameter.IsDefault = true;
  }
  if (_parameter.Id) {
    const parameter = editParameters.value.find((x) => x.Id == _parameter.Id);
    parameter.Name = _parameter.Name;
    parameter.Color = _parameter.Color;
  } else {
    editParameters.value.unshift(_parameter);
  }
};
/**Создание нового шаблона */
const createTemplate = async (template: ParametersTemplateModel) => {
  const response = await templateService.UpsertTemplate(template);
  if (response.IsSuccess) {
    getTemplates();
  }
};
/**Получение списка шаблонов */
const getTemplates = async () => {
  const response = await templateService.GetTemplateList(props.type);
  if (response.IsSuccess) {
    templates.value = response.Value;
  }
};
/**Удалить щаблон */
const removeTemplate = async (template: ParametersTemplateModel) => {
  const response = await templateService.RemoveTemplate(template.Id);
  if (response.IsSuccess) {
    templates.value = templates.value.filter((t) => t.Id != template.Id);
  }
};
/**Сохранить изменения */
const saveChanges = async () => {
  const response = await apiService.value.SetList(props.entityId, editParameters.value);

  if (isNamespaceParameter.value) sendUsedParamsRemoveNotification();
  if (response.IsSuccess) setParametersArrays(response.Value);
};
/**Отправка уведомлений об удалении использовавшихся параметров */
const sendUsedParamsRemoveNotification = () => {
  const removedParameters = parameters.value.filter(
    (p) => p.InUse && !editParameters.value.some((ep) => ep.Id == p.Id),
  );
  const basicParameter = editParameters.value.find((p) => p.IsDefault);
  removedParameters.forEach((parameter) => {
    TirPmNotificationEventBus.emit(
      "add",
      new TirPmNotificationModel({
        title: "Изменения внесены",
        subtitle: `Атрибут "${parameter?.Name}" удален и заменен в тикетах на атрибут "${basicParameter.Name}"`,
        type: "success",
        group: "general",
        life: 6000,
        colorIcon: "var(--status-inverted-positive)",
      }),
    );
  });
};
/**Установка значений переменных с параметрами */
const setParametersArrays = (array: TaskParameterModel[]) => {
  parameters.value = array;
  editParameters.value = array.map((p) => new TaskParameterModel(p));
};
/**Изменить имя шаблона */
const upsertTemplate = (template: ParametersTemplateModel) => {
  templateService.UpsertTemplate(template);
};
/**Выбор шаблона */
const selectParameters = (parameters: TaskParameterModel[]) => {
  currentTab.value = 0;

  const tempId = new Date().getTime();

  editParameters.value = editParameters.value.filter((p) => p.InUse);
  parameters.forEach((parameter, index) => {
    if (
      !editParameters.value.some(
        (p) => p.Name.toLocaleLowerCase() == parameter.Name.toLocaleLowerCase(),
      )
    ) {
      editParameters.value.push(
        new TaskParameterModel({
          ...parameter,
          InUse: false,
          IsDefault: false,
          Id: undefined,
          TempId: tempId + index,
        }),
      );
    }
  });
  const haveDefault = editParameters.value.some((p) => p.IsDefault);
  if (!haveDefault && editParameters.value.length) {
    editParameters.value[0].IsDefault = true;
  }
};
/**Очистка неиспользуемых параметров */
const clearUnused = () => {
  editParameters.value = editParameters.value.filter((parameter) => parameter.InUse);
};
/**Выбор редактируемого шаблона */
const setEditingTemplate = (template?: ParametersTemplateModel) => {
  editingTemplate.value = template;
};
/**Сохранить шаблон */
const editTemplate = (template: ParametersTemplateModel) => {
  upsertTemplate(template);
  Object.assign(editingTemplate.value, template);
  setEditingTemplate();
};
/**Открыть форму для импорты шаблонов */
const setImportForm = (_isImport: boolean) => {
  isImport.value = _isImport;
  setTimeout(() => emit("import", _isImport));
};
/**Импортировать параметры */
const importParameters = (parameters: TaskParameterModel[]) => {
  setImportForm(false);
  selectParameters(parameters);
};
/**Открыть окно с подтверждением выхода из настройки */
const openLeaveConfirmation = (callback?: Function) => {
  $taskParametersLeave.value?.toggle(true, callback);
};

/**Код для использования по ref */
defineExpose({
  haveChanges,
  setImportForm,
  openLeaveConfirmation,
});
</script>
<template>
  <div class="task-parameters-form">
    <div class="task-parameters-form_main">
      <TemplateEditForm
        v-if="editingTemplate"
        :type="type"
        :template="editingTemplate"
        :title="title"
        :templates="templates"
        :namespaceName="namespaceName"
        @cancel="setEditingTemplate"
        @save="editTemplate"
      />
      <TemplateImportForm
        v-else-if="isImport"
        :type="type"
        @import="importParameters"
        @back="setImportForm(false)"
      />
      <template v-else>
        <div class="task-parameters-form_title">
          <Tabs v-model="currentTab" :variant="TirPmTabsVariantEnum.Secondary">
            <Tab
              v-for="(tab, idx) in tabs"
              :key="idx"
              :val="tab.Id"
              :label="tab.Name"
              :currentTab="currentTab"
            />
          </Tabs>
          <TirPmButton
            class="task-parameters-form_title_save"
            :variant="TirPmButtonVariantEnum.PrimaryState"
            :size="TirPmButtonSizeEnum.Small"
            :isDisabled="!haveChanges"
            @click="saveChanges"
          >
            Применить изменения
          </TirPmButton>
        </div>
        <template v-if="currentTab == 0">
          <ParametersEditForm
            :type="type"
            :templates="templates"
            :editParameters="editParameters"
            :createTemplateDisabled="haveChanges"
            :title="title"
            :namespaceName="namespaceName"
            @upsert="onUpsertParameter"
            @clearUnused="clearUnused"
            @createTemplate="createTemplate"
            @selectTemplate="selectParameters"
          />
        </template>
        <template v-if="currentTab == 1">
          <TemplatesListForm
            :templates="templates"
            :type="type"
            @select="selectParameters"
            @upsert="upsertTemplate"
            @editCompound="setEditingTemplate"
            @remove="removeTemplate"
            @import="setImportForm(true)"
          />
        </template>
      </template>
    </div>
  </div>
  <TaskParametersLeave ref="$taskParametersLeave" />
</template>
<style lang="scss">
.task-parameters-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: 0.5rem;
  gap: 3rem;
  &_main {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 2.25rem;
  }
  &_title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    &_save {
      margin-left: auto;
    }
  }
  &_button {
    width: 2rem;
    height: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--text-primary, #272d37);
    border-radius: 0.375rem;
    transition: 0.2s;
    cursor: pointer;
    & > svg {
      transition: 0.2s;
      width: 1rem;
      height: 1rem;
    }
    &:hover {
      background-color: var(--neutral-translucent-5);
    }
    &.active {
      background-color: var(--neutral-translucent-10);
      &:hover {
        background-color: var(--neutral-translucent-20);
      }
    }
  }
}
</style>
